import { JsonRpcMcpClient } from './McpClient.ts'

export type JSONRecord = Record<string, unknown>

export interface ChatToolCall {
  id: string
  name: string
  input: JSONRecord
}

export interface ChatToolDescriptor {
  type: 'function'
  name: string
  execution: 'server'
  description?: string
  parameters?: JSONRecord
}

export interface ClientToolDescriptor {
  type: 'function'
  name: string
  execution: 'client'
  description?: string
  parameters?: JSONRecord
}

export interface ToolResult {
  toolCallId: string
  name?: string
  content: string
  isError?: boolean
}

export interface ToolRoute {
  kind: string
  method?: string
  path?: string
  mcpToolName?: string
}

export interface ToolCatalogEntry {
  tool?: {
    name?: string
    description?: string
    inputSchema?: JSONRecord
  }
  route?: ToolRoute
}

export interface AgentToolRuntimeOptions {
  postParty: (path: string, body: JSONRecord) => Promise<JSONRecord>
  postPartyRaw: (path: string, body: JSONRecord) => Promise<JSONRecord>
}

export class AgentToolRuntime {
  #postParty: (path: string, body: JSONRecord) => Promise<JSONRecord>
  #postPartyRaw: (path: string, body: JSONRecord) => Promise<JSONRecord>
  #toolCatalog: ToolCatalogEntry[] = []
  #toolRoutes = new Map<string, ToolRoute>()
  #mcpClients = new Map<string, JsonRpcMcpClient>()

  constructor(options: AgentToolRuntimeOptions) {
    this.#postParty = options.postParty
    this.#postPartyRaw = options.postPartyRaw
  }

  get toolCatalog(): ToolCatalogEntry[] {
    return this.#toolCatalog
  }

  async refreshCatalog(): Promise<void> {
    const catalog = await this.#postParty('/ai/tools/catalog', {})
    this.setCatalog(catalog.tools)
  }

  setCatalog(value: unknown): void {
    const toolCatalog = Array.isArray(value) ? value : []
    this.#toolCatalog = toolCatalog as ToolCatalogEntry[]
    this.#toolRoutes = new Map(
      this.#toolCatalog
        .filter((entry) => entry.tool?.name && entry.route)
        .map((entry) => [entry.tool!.name!, entry.route!]),
    )
    this.#mcpClients.clear()
  }

  chatTools(options: {
    clientTools?: ClientToolDescriptor[]
  } = {}): Array<ChatToolDescriptor | ClientToolDescriptor> {
    return [
      ...this.#toolCatalog.map(toChatTool).filter(isDefined),
      ...(options.clientTools ?? []),
    ]
  }

  async executeToolCalls(toolCalls: ChatToolCall[]): Promise<ToolResult[]> {
    const results: ToolResult[] = []
    for (const call of toolCalls) {
      try {
        results.push({
          toolCallId: call.id,
          name: call.name,
          content: await this.executeToolCall(call),
        })
      } catch (e) {
        results.push({
          toolCallId: call.id,
          name: call.name,
          content: String(e),
          isError: true,
        })
      }
    }
    return results
  }

  async executeToolCall(call: ChatToolCall): Promise<string> {
    const route = this.#toolRoutes.get(call.name)
    if (!route) throw new Error(`No discovered tool named ${call.name}`)
    if (route.method !== 'POST' || !route.path) {
      throw new Error(`Unsupported route for tool ${call.name}`)
    }

    if (route.kind === 'platform') {
      const result = await this.#postParty(route.path, call.input)
      return JSON.stringify(result)
    }

    if (route.kind === 'mcp') {
      return await this.#executeMcpToolCall(route, call)
    }

    throw new Error(`Unsupported route kind for tool ${call.name}`)
  }

  async #executeMcpToolCall(
    route: ToolRoute,
    call: ChatToolCall,
  ): Promise<string> {
    let client = this.#mcpClients.get(route.path!)
    if (!client) {
      client = new JsonRpcMcpClient({
        post: (body) => this.#postPartyRaw(route.path!, body),
      })
      this.#mcpClients.set(route.path!, client)
    }
    const result = await client.callTool(route.mcpToolName || call.name, {
      ...call.input,
    })
    return JSON.stringify(result)
  }
}

/**
 * The subset of an {@link AgentToolRuntime} that a chat session uses: refresh
 * the catalog, list tools for the model, and execute a tool call. Implemented
 * by both the single-party {@link AgentToolRuntime} and the multi-party
 * {@link AggregateToolRuntime}, so a session can hold either transparently.
 */
export interface SessionToolRuntime {
  refreshCatalog(): Promise<void>
  chatTools(
    options?: { clientTools?: ClientToolDescriptor[] },
  ): Array<ChatToolDescriptor | ClientToolDescriptor>
  executeToolCall(call: ChatToolCall): Promise<string>
}

/** One tool provider's runtime plus an optional allow-list of tool names. */
export interface ToolProviderRuntime {
  /** Stable key used to namespace colliding tool names (e.g. the party host). */
  key: string
  runtime: SessionToolRuntime
  /** Tool names exposed to chats; undefined means all of the provider's tools. */
  allowedTools?: Set<string>
}

/**
 * Aggregates the tool runtimes of several providers into one runtime. Only
 * allowed tools are exposed; when the same tool name occurs on more than one
 * provider it is namespaced (`<index>__<name>`) so the model can address each,
 * and execution is routed back to the owning provider's runtime under the bare
 * name.
 *
 * Each provider runtime carries its own request context (e.g. a forwarded chat
 * initiator), so execution authorizes against that context on each provider.
 */
export class AggregateToolRuntime implements SessionToolRuntime {
  #providers: ToolProviderRuntime[]
  #routing = new Map<string, { provider: ToolProviderRuntime; realName: string }>()

  constructor(providers: ToolProviderRuntime[]) {
    this.#providers = providers
  }

  async refreshCatalog(): Promise<void> {
    await Promise.all(
      this.#providers.map((provider) => provider.runtime.refreshCatalog()),
    )
  }

  chatTools(options: {
    clientTools?: ClientToolDescriptor[]
  } = {}): Array<ChatToolDescriptor | ClientToolDescriptor> {
    this.#routing.clear()

    const perProvider = this.#providers.map((provider) =>
      provider.runtime.chatTools().filter((tool): tool is ChatToolDescriptor =>
        tool.execution === 'server' && this.#isAllowed(provider, tool.name)
      )
    )

    const occurrences = new Map<string, number>()
    for (const tools of perProvider) {
      for (const tool of tools) {
        occurrences.set(tool.name, (occurrences.get(tool.name) ?? 0) + 1)
      }
    }

    const exposed: ChatToolDescriptor[] = []
    this.#providers.forEach((provider, index) => {
      for (const tool of perProvider[index]) {
        const collides = (occurrences.get(tool.name) ?? 0) > 1
        const exposedName = collides ? `${index}__${tool.name}` : tool.name
        this.#routing.set(exposedName, { provider, realName: tool.name })
        exposed.push({ ...tool, name: exposedName })
      }
    })

    return [...exposed, ...(options.clientTools ?? [])]
  }

  async executeToolCall(call: ChatToolCall): Promise<string> {
    const route = this.#routing.get(call.name)
    if (!route) throw new Error(`No discovered tool named ${call.name}`)
    return await route.provider.runtime.executeToolCall({
      ...call,
      name: route.realName,
    })
  }

  #isAllowed(provider: ToolProviderRuntime, name: string): boolean {
    return !provider.allowedTools || provider.allowedTools.has(name)
  }
}

/**
 * A {@link SessionToolRuntime} backed by another party's MCP gateway
 * (`/mcp/rpc`), spoken over JSON-RPC. Used for cross-party tool providers: the
 * caller mints a signed token and forwards the chat initiator's context, and the
 * remote gateway returns the grant-filtered catalog and runs the tool. Only MCP
 * tools are surfaced this way (no platform memory tools).
 */
export class GatewayToolRuntime implements SessionToolRuntime {
  #client: JsonRpcMcpClient
  #tools: ChatToolDescriptor[] = []

  constructor(options: { postRpc: (body: JSONRecord) => Promise<JSONRecord> }) {
    this.#client = new JsonRpcMcpClient({ post: options.postRpc })
  }

  async refreshCatalog(): Promise<void> {
    const tools = await this.#client.listTools()
    this.#tools = tools.map(toGatewayChatTool).filter(isDefined)
  }

  chatTools(options: {
    clientTools?: ClientToolDescriptor[]
  } = {}): Array<ChatToolDescriptor | ClientToolDescriptor> {
    return [...this.#tools, ...(options.clientTools ?? [])]
  }

  async executeToolCall(call: ChatToolCall): Promise<string> {
    const result = await this.#client.callTool(call.name, { ...call.input })
    return JSON.stringify(result)
  }
}

function toGatewayChatTool(tool: unknown): ChatToolDescriptor | null {
  const descriptor = tool as {
    name?: string
    description?: string
    inputSchema?: JSONRecord
  }
  if (!descriptor?.name) return null
  return {
    type: 'function',
    name: descriptor.name,
    execution: 'server',
    description: descriptor.description,
    parameters: descriptor.inputSchema,
  }
}

export function toChatTool(
  entry: ToolCatalogEntry,
): ChatToolDescriptor | null {
  if (!entry?.tool?.name) return null
  return {
    type: 'function',
    name: entry.tool.name,
    execution: 'server',
    description: entry.tool.description,
    parameters: entry.tool.inputSchema,
  }
}

/**
 * Combines an app's once-authored, app-level description (`appDescription`) with
 * a tool's own short description. The app-level "when to use this expert" context
 * leads, then the per-tool pointer. Falls back to whichever single value exists,
 * and skips the join when the two are equal or no app description is present
 * (e.g. platform tools). Applied at the platform catalog layer so every surface
 * (chat tool list, MCP gateway, team catalog) shares one composed description.
 */
export function composeToolDescription(
  toolDescription?: string,
  appDescription?: string,
): string | undefined {
  if (!appDescription || appDescription === toolDescription) return toolDescription
  if (!toolDescription) return appDescription
  return `${appDescription}\n\n${toolDescription}`
}

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}
